import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Typography, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getUsuarioById, createUsuario, updateUsuario } from '../services/usuarioService';
import { useAuth } from '../context/AuthContext';
import config from '../config';

function UsuarioHomeForm() {
  const [usuario, setUsuario] = useState({
    username: '',
    password_hash: '',
    id_pais: '',
    id_tipo_autenticacion: '',
    id_nivel_usuario: '',
    trabajador: {
      nombre_trabajador: '',
      apellido_trabajador: '',
      tipo_documento: '',
      numero_documento: '',
      direccion_trabajador: '',
      telefono_trabajador: '',
      email_trabajador: '',
    },
  });

  const [paises, setPaises] = useState([]);
  const [tiposAutenticacion, setTiposAutenticacion] = useState([]);
  const [nivelesUsuario, setNivelesUsuario] = useState([]);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchPaises();
    fetchTiposAutenticacion();
    fetchNivelesUsuario();
    if (id) {
      fetchUsuario(id);
    }
  }, [id]);

  const fetchPaises = async () => {
    const response = await axios.get(`${config.API_BASE_URL}/api/paises`);
    setPaises(response.data);
  };

  const fetchTiposAutenticacion = async () => {
    const response = await axios.get(`${config.API_BASE_URL}/api/general/tipoautenticacion`);
    setTiposAutenticacion(response.data);
  };

  const fetchNivelesUsuario = async () => {
    const response = await axios.get(`${config.API_BASE_URL}/api/general/nivelusuario`);
    setNivelesUsuario(response.data);
  };

  const fetchUsuario = async (id) => {
    const data = await getUsuarioById(id, user.token);
    // Asegura que el objeto trabajador esté definido incluso al editar un usuario
    setUsuario(prev => ({
      ...data,
      trabajador: data.trabajador || {
        nombre_trabajador: '',
        apellido_trabajador: '',
        tipo_documento: '',
        numero_documento: '',
        direccion_trabajador: '',
        telefono_trabajador: '',
        email_trabajador: '',
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('trabajador.')) {
      const trabajadorField = name.split('.')[1];
      setUsuario(prev => ({
        ...prev,
        trabajador: {
          ...prev.trabajador,
          [trabajadorField]: value,
        },
      }));
    } else {
      setUsuario({ ...usuario, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      if (id) {
        await updateUsuario(id, usuario, user.token);
      } else {
        await createUsuario({ ...usuario, id_empresa: user.id_empresa }, user.token);
      }
      navigate('/home/usuarios');
    } catch (error) {
      console.error('Error al guardar el usuario:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Typography variant="h4" gutterBottom>
        {id ? 'Editar Usuario' : 'Crear Usuario'}
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          label="Nombre de Usuario"
          variant="outlined"
          fullWidth
          margin="normal"
          name="username"
          value={usuario.username}
          onChange={handleChange}
          required
        />
        <TextField
          label="Contraseña"
          variant="outlined"
          fullWidth
          margin="normal"
          name="password_hash"
          type="password"
          value={usuario.password_hash}
          onChange={handleChange}
          required
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>País</InputLabel>
          <Select
            name="id_pais"
            value={usuario.id_pais}
            onChange={handleChange}
            required
          >
            {paises.map((pais) => (
              <MenuItem key={pais.id_pais} value={pais.id_pais}>
                {pais.det_pais}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Tipo de Autenticación</InputLabel>
          <Select
            name="id_tipo_autenticacion"
            value={usuario.id_tipo_autenticacion}
            onChange={handleChange}
            required
          >
            {tiposAutenticacion.map((tipo) => (
              <MenuItem key={tipo.id_tipo_autenticacion} value={tipo.id_tipo_autenticacion}>
                {tipo.det_tipo_autenticacion}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Nivel de Usuario</InputLabel>
          <Select
            name="id_nivel_usuario"
            value={usuario.id_nivel_usuario}
            onChange={handleChange}
            required
          >
            {nivelesUsuario.map((nivel) => (
              <MenuItem key={nivel.id_nivel_usuario} value={nivel.id_nivel_usuario}>
                {nivel.det_nivel_usuario}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* Datos del Trabajador */}
        {usuario.id_nivel_usuario <= 3 && (
          <>
            <TextField
              label="Nombre"
              variant="outlined"
              fullWidth
              margin="normal"
              name="trabajador.nombre_trabajador"
              value={usuario.trabajador.nombre_trabajador}
              onChange={handleChange}
              required={usuario.id_nivel_usuario <= 3}
            />
            <TextField
              label="Apellido"
              variant="outlined"
              fullWidth
              margin="normal"
              name="trabajador.apellido_trabajador"
              value={usuario.trabajador.apellido_trabajador}
              onChange={handleChange}
              required={usuario.id_nivel_usuario <= 3}
            />
            <TextField
              label="Teléfono"
              variant="outlined"
              fullWidth
              margin="normal"
              name="trabajador.telefono_trabajador"
              value={usuario.trabajador.telefono_trabajador}
              onChange={handleChange}
            />
          </>
        )}

        <Button variant="contained" color="primary" type="submit" fullWidth style={{ marginTop: '20px' }} disabled={loading}>
          {loading ? 'Guardando...' : (id ? 'Guardar Cambios' : 'Crear Usuario')}
        </Button>
      </form>
    </Container>
  );
}

export default UsuarioHomeForm;
